import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { 
  ArrowRight, 
  Users, 
  UserCheck, 
  Briefcase,
  Target,
  TrendingUp,
  Brain,
  CheckCircle,
  Building2
} from 'lucide-react';
import { images } from '../data/mock';

const WorkforceSolutionsPage = () => {
  const services = [
    {
      icon: Users,
      title: "Flexible Talent Models",
      description: "Access the right talent through models designed for your specific needs and growth stage.",
      items: [
        "Strategic staff augmentation for technology, product, data, and transformation teams",
        "Contract-to-hire models to reduce hiring risk and ensure cultural fit",
        "Direct hire and full-time placement aligned to long-term growth objectives",
        "Onshore, offshore, and hybrid team configurations"
      ]
    },
    {
      icon: Target,
      title: "Recruitment Process Outsourcing (RPO)",
      description: "End-to-end recruitment ownership that scales with your hiring needs while maintaining quality and speed.",
      items: [
        "Full-cycle recruitment ownership: sourcing, screening, coordination, and hiring support",
        "Scalable RPO models designed for startups, scale-ups, and enterprises",
        "Transparent, outcome-driven hiring aligned to speed, quality, and candidate experience",
        "Dedicated recruitment teams embedded in your hiring process",
        "Employer branding and candidate engagement strategies"
      ],
      highlight: true
    },
    {
      icon: Brain,
      title: "AI-Assisted Recruitment (Human-Led)",
      description: "Leverage AI to enhance recruitment efficiency while maintaining human judgment and oversight.",
      items: [
        "AI-enabled sourcing, screening, and candidate matching as assistive tools",
        "Human oversight at every decision point to ensure fairness and quality",
        "Designed to improve efficiency without compromising candidate experience",
        "Bias detection and mitigation in recruitment workflows"
      ]
    },
    {
      icon: Briefcase,
      title: "Fractional Leadership & Advisory",
      description: "Access senior talent leadership without full-time commitment.",
      items: [
        "Fractional CXO and senior talent leaders for strategic guidance",
        "Hiring strategy development and workforce planning",
        "Transformation leadership for scaling organizations",
        "Ideal for organizations scaling without full-time leadership overhead"
      ]
    },
    {
      icon: TrendingUp,
      title: "Workforce Modernization & Upskilling",
      description: "Build future-ready capabilities within your existing workforce.",
      items: [
        "Capability building and workforce transformation initiatives",
        "Future-ready skill development aligned to evolving technology needs",
        "Learning and development program design",
        "Career pathway development and succession planning"
      ]
    }
  ];

  const differentiators = [
    {
      title: "Strategic Partner, Not Vendor",
      description: "We invest in understanding your business, culture, and goals to deliver talent solutions that truly fit."
    },
    {
      title: "Quality Over Volume",
      description: "Our focus is on finding the right talent, not just filling seats. Every candidate is carefully vetted for skills and cultural alignment."
    },
    {
      title: "Transparent Process",
      description: "Clear communication, honest timelines, and full visibility into our recruitment process from start to finish."
    },
    {
      title: "Technology-Enabled, Human-Led",
      description: "We leverage AI and automation to enhance efficiency, but human judgment drives every hiring decision."
    }
  ];

  return (
    <div className="min-h-screen pt-24">
      <SEO 
        title="Workforce & Talent Solutions"
        description="Strategic workforce and talent solutions from TekClap. RPO, staff augmentation, direct hire, fractional leadership, and AI-assisted recruitment with human oversight."
        keywords="workforce solutions, talent solutions, recruitment process outsourcing, RPO, staff augmentation, direct hire, fractional leadership, talent acquisition"
      />

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#FFF4E6] to-[#FAFAF8]">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#F58B23] font-medium text-sm uppercase tracking-wider mb-4 block">
                Workforce & Talent Solutions
              </span>
              <h1 className="text-4xl md:text-5xl font-serif font-bold text-[#1A1A1A] mb-6">
                Strategic Workforce Solutions That Scale With Your Business
              </h1>
              <p className="text-lg text-[#6B7280] leading-relaxed mb-8">
                TekClap is your strategic workforce partner, not a transactional staffing vendor. 
                We provide flexible talent models, end-to-end recruitment process outsourcing (RPO), 
                and workforce modernization services designed for organizations that value quality, 
                speed, and long-term partnership.
              </p>
              <Link to="/contact">
                <Button className="bg-[#F58B23] hover:bg-[#D97706] text-white font-medium px-8 py-6 text-base">
                  Schedule Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img 
                src={images.talent} 
                alt="Workforce Solutions" 
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-lg">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#FFF4E6] rounded-full flex items-center justify-center">
                    <UserCheck className="w-6 h-6 text-[#F58B23]" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-[#1A1A1A]">150+</div>
                    <div className="text-sm text-[#6B7280]">Experts Placed</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#1A1A1A] mb-6">
              Comprehensive Workforce & Talent Solutions
            </h2>
            <p className="text-lg text-[#6B7280]">
              From strategic staffing to full recruitment outsourcing, we offer flexible 
              models designed to meet your unique hiring and workforce needs.
            </p>
          </div>

          <div className="space-y-8">
            {services.map((service, index) => (
              <div 
                key={index}
                className={`rounded-2xl p-8 md:p-10 border ${
                  service.highlight 
                    ? 'bg-gradient-to-r from-[#FFF4E6] to-[#FAFAF8] border-[#F58B23]/30' 
                    : 'bg-[#FAFAF8] border-gray-100'
                }`}
              >
                <div className="grid md:grid-cols-3 gap-8">
                  <div className="md:col-span-1">
                    <div className="flex items-center gap-4 mb-4">
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                        service.highlight ? 'bg-[#F58B23]' : 'bg-[#FFF4E6]'
                      }`}>
                        <service.icon className={`w-7 h-7 ${
                          service.highlight ? 'text-white' : 'text-[#F58B23]'
                        }`} />
                      </div>
                      {service.highlight && (
                        <span className="text-xs font-medium text-[#F58B23] bg-white px-3 py-1 rounded-full">
                          Featured
                        </span>
                      )}
                    </div>
                    <h3 className="text-2xl font-semibold text-[#1A1A1A] mb-3">
                      {service.title}
                    </h3>
                    <p className="text-[#6B7280]">
                      {service.description}
                    </p>
                  </div>
                  <div className="md:col-span-2">
                    <ul className="grid sm:grid-cols-2 gap-4">
                      {service.items.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-[#374151]">
                          <CheckCircle className="w-5 h-5 text-[#F58B23] flex-shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why TekClap for Workforce */}
      <section className="py-20 bg-[#FAFAF8]">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#1A1A1A] mb-6">
              Why Organizations Choose TekClap
            </h2>
            <p className="text-lg text-[#6B7280]">
              We approach workforce solutions differently—as strategic partners invested in your success.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {differentiators.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-sm">
                <div className="w-10 h-10 bg-[#FFF4E6] rounded-lg flex items-center justify-center mb-4">
                  <Building2 className="w-5 h-5 text-[#F58B23]" />
                </div>
                <h3 className="text-xl font-semibold text-[#1A1A1A] mb-3">
                  {item.title}
                </h3>
                <p className="text-[#6B7280]">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1A1A1A]">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-6">
            Let&apos;s Design a Workforce Model That Scales With Your Business
          </h2>
          <p className="text-lg text-gray-300 mb-10 max-w-2xl mx-auto">
            Whether you need strategic staffing, full RPO services, or workforce transformation support, 
            we&apos;re ready to help you build the team you need.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-[#F58B23] hover:bg-[#D97706] text-white font-medium px-8 py-6 text-base">
                Schedule Consultation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/client-impact">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#1A1A1A] font-medium px-8 py-6 text-base">
                View Client Impact
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <h2 className="text-2xl font-serif font-bold text-[#1A1A1A] mb-8">
            Explore Related Services
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/technology-consulting" className="group p-6 bg-[#FAFAF8] rounded-xl hover:shadow-md transition-all">
              <h3 className="font-semibold text-[#1A1A1A] mb-2 group-hover:text-[#F58B23] transition-colors">
                Technology Consulting →
              </h3>
              <p className="text-sm text-[#6B7280]">
                Digital transformation, technology strategy, and engineering services.
              </p>
            </Link>
            <Link to="/ai-consulting" className="group p-6 bg-[#FAFAF8] rounded-xl hover:shadow-md transition-all">
              <h3 className="font-semibold text-[#1A1A1A] mb-2 group-hover:text-[#F58B23] transition-colors">
                AI Consulting →
              </h3>
              <p className="text-sm text-[#6B7280]">
                AI strategy, intelligent automation, and AI-enabled workflows.
              </p>
            </Link>
            <Link to="/product-development" className="group p-6 bg-[#FAFAF8] rounded-xl hover:shadow-md transition-all">
              <h3 className="font-semibold text-[#1A1A1A] mb-2 group-hover:text-[#F58B23] transition-colors">
                Product Development →
              </h3>
              <p className="text-sm text-[#6B7280]">
                Product strategy, cloud-native SaaS, and custom engineering.
              </p>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WorkforceSolutionsPage;
